import { useEffect, useState } from 'react';

// Generate a simple version hash from build time
const CURRENT_VERSION = import.meta.env.MODE === 'development' 
  ? 'dev' 
  : Date.now().toString();

/**
 * Hook to check if a new version of the app is deployed
 * Automatically prompts user to reload when new version detected
 */
export const useVersionCheck = (checkIntervalMinutes: number = 10) => {
  const [newVersionAvailable, setNewVersionAvailable] = useState(false);

  useEffect(() => {
    // Skip in development
    if (import.meta.env.MODE === 'development') {
      return;
    }

    const checkVersion = async () => {
      try {
        // Fetch the current index.html with cache-busting
        const response = await fetch(`/?v=${Date.now()}`, {
          method: 'HEAD',
          cache: 'no-cache'
        });

        // Check if ETag or Last-Modified changed
        const etag = response.headers.get('etag');
        const lastModified = response.headers.get('last-modified');
        
        const storedEtag = localStorage.getItem('app-etag');
        const storedLastModified = localStorage.getItem('app-last-modified');

        // First time - store values
        if (!storedEtag && !storedLastModified) {
          if (etag) localStorage.setItem('app-etag', etag);
          if (lastModified) localStorage.setItem('app-last-modified', lastModified);
          return;
        }

        // Check if changed
        const hasChanged = 
          (etag && etag !== storedEtag) ||
          (lastModified && lastModified !== storedLastModified);

        if (hasChanged) {
          console.log('🔄 New version detected!');
          setNewVersionAvailable(true);
          
          // Update stored values
          if (etag) localStorage.setItem('app-etag', etag);
          if (lastModified) localStorage.setItem('app-last-modified', lastModified);
        }
      } catch (error) {
        console.error('Version check failed:', error);
      }
    };

    // Check on mount
    checkVersion();

    // Check periodically
    const interval = setInterval(checkVersion, checkIntervalMinutes * 60 * 1000);

    return () => clearInterval(interval);
  }, [checkIntervalMinutes]);

  const reloadApp = () => {
    window.location.reload();
  };

  return { newVersionAvailable, reloadApp };
};
